import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { IBudget } from "../../../../models/budgetV2";
import { decideBudgetV2, fetchBudgetVersionsV2, updateBudgetV2 } from "../../../../api/fetchBudgetV2";
import { BudgetDecision } from "../../enums/budgetDecision";
import { buildBudgetRequestFromBudget } from "../utils/buildBudgetRequestFromBudget";

export interface IUseBudgetEditActionsOverrides {
  onSaved?: (budgetId: string, latestVersion: number) => void;
  onDecided?: () => void;
}

export const useBudgetEditActions = (
  budget: IBudget | null,
  budgetVersion?: number,
  uploadPendingAttachments?: () => Promise<boolean>,
  overrides?: IUseBudgetEditActionsOverrides,
  originalBudget?: IBudget | null
) => {
  const navigate = useNavigate();
  const location = useLocation();

  // Always land on the kanban (/<persona>/budgets-kanban), regardless of the persona or the screen we came from.
  const goToKanban = () => {
    const match = location.pathname.match(/^(.*?\/budgets(?:-kanban)?)(?:\/|$)/);
    navigate(match ? match[1] : "..");
  };
  const [submitting, setSubmitting] = useState(false);

  const navigateToLatestBudgetDetails = async (uuid: string) => {
    const versions = await fetchBudgetVersionsV2(uuid);
    const latestVersion = versions.reduce((max, item) => {
      const versionNumber = item.versionNumber ?? 0;
      return versionNumber > max ? versionNumber : max;
    }, budgetVersion ?? 0);

    if (overrides?.onSaved) {
      overrides.onSaved(uuid, latestVersion);
    } else {
      goToKanban();
    }
  };

  const saveBudgetUpdate = async () => {
    if (!budget?.uuid) return;
    setSubmitting(true);
    try {
      if (uploadPendingAttachments) {
        const uploaded = await uploadPendingAttachments();
        if (!uploaded) return;
      }
      await updateBudgetV2(budget.uuid, buildBudgetRequestFromBudget(budget, budget.occurrenceUuid, originalBudget));
      await navigateToLatestBudgetDetails(budget.uuid);
    } finally {
      setSubmitting(false);
    }
  };

  const acceptBudget = async () => {
    if (!budget?.uuid) return;
    setSubmitting(true);
    try {
      await decideBudgetV2(budget.uuid, {
        decision: BudgetDecision.APPROVE,
        counterProposalObservation: null,
        counterProposalValue: budget.versionData?.counterProposalValue ?? null,
        services: [],
      });
      if (overrides?.onDecided) {
        overrides.onDecided();
      } else {
        goToKanban();
      }
    } finally {
      setSubmitting(false);
    }
  };

  const counterProposeBudget = async (counterProposalValue: number, counterProposalObservation: string) => {
    if (!budget?.uuid) return;
    setSubmitting(true);
    try {
      await decideBudgetV2(budget.uuid, {
        decision: BudgetDecision.COUNTER_PROPOSE,
        counterProposalObservation: counterProposalObservation || null,
        counterProposalValue,
        services: [],
      });
      if (overrides?.onDecided) {
        overrides.onDecided();
      } else {
        goToKanban();
      }
    } finally {
      setSubmitting(false);
    }
  };

  const rejectBudget = async (rejectionObservation: string) => {
    if (!budget?.uuid) return;
    setSubmitting(true);
    try {
      await decideBudgetV2(budget.uuid, {
        decision: BudgetDecision.REJECT,
        counterProposalObservation: rejectionObservation,
        counterProposalValue: budget.versionData?.counterProposalValue ?? null,
        services: [],
      });
      if (overrides?.onDecided) {
        overrides.onDecided();
      } else {
        goToKanban();
      }
    } finally {
      setSubmitting(false);
    }
  };

  return {
    submitting,
    saveBudgetUpdate,
    acceptBudget,
    rejectBudget,
    counterProposeBudget,
  };
};
